import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import {
	AlarisComplexTableComponent,
	AlarisDialogService,
	AlarisLanguageService,
	AlarisTableStateService,
	RowActionSimple,
	TABLE_STATE_SERVICE_INJECTOR
} from '@campaign-portal/components-library';
import { TableSettingsService } from '@helpers/api/table-settings.service';
import { TableTypes } from '@campaign-portal/namespace/entities/table-settings/specs';
import { BehaviorSubject } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UserService } from 'src/app/auth/user.service';
import { FilterType } from '@campaign-portal/namespace/common/rpc.params';
import { UsersService } from '../users/users.service';
import { AuthGuardService } from 'src/app/auth/auth.guard';
import { SimulationFieldsService } from './simulation.fields.service';
import { CreateSimulationComponent } from './create-simulation/create-simulation.component';
import { SimulationDeleteDialogComponent } from './dialog/dialog.component';
import { Simulation, SiTask } from '@helpers/types/models';
import { SimulationService } from './simulation.service';
import { SimulationResultsComponent } from './simulation-results/simulation-results.component';
import { ProductsService } from '@helpers/repo/products.service';

@Component({
	selector: 'app-simulation',
	standalone: false,
	templateUrl: './simulation.component.html',
	styleUrls: [
		'./simulation.component.scss',
		'../../../../node_modules/@campaign-portal/components-library/src/assets/templates/table-complex/complex-table.component.scss'
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [
		AlarisTableStateService,
		{ provide: TABLE_STATE_SERVICE_INJECTOR, useExisting: AlarisTableStateService }
	]
})
export class SimulationComponent extends AlarisComplexTableComponent<Simulation> implements OnInit {
	override readonly rowActions: RowActionSimple<any>[] = [
		{
			icon: 'icon-info',
			label: 'actions.view',
			action: this.viewResults.bind(this),
			highlight: true,
			separator: true,
			disabled: (): boolean => {
				return !this.user.allowed(['simulations.read']);
			}
		},
		{
			icon: 'icon-delete',
			label: 'actions.delete',
			action: this.delete.bind(this),
			highlight: true,
			separator: true,
			disabled: (): boolean => {
				return !this.user.allowed(['simulations.edit']);
			}
		}
	];

	readonly products$ = new BehaviorSubject<{ name: string; value: number }[]>([]);
	readonly productsNames$ = new BehaviorSubject<string[]>([]);
	readonly exporting$ = new BehaviorSubject<boolean>(false);

	constructor(
		public override readonly fieldsService: SimulationFieldsService,
		tableSettingsService: TableSettingsService,
		readonly simulationService: SimulationService,
		protected readonly user: UserService,
		private readonly dialog: AlarisDialogService,
		private readonly users: UsersService,
		private readonly langService: AlarisLanguageService,
		private readonly productsService: ProductsService,
		private readonly authGuardService: AuthGuardService,
	) {
		super(simulationService, fieldsService, tableSettingsService, TableTypes.INTERNAL);
		this.state.filters()
			.set(this.fieldsService.id.variable, {enabled: true, filterType: FilterType.LIKE})
			.set(this.fieldsService.status.variable, {enabled: false, filterType: FilterType.LIKE})
			.set(this.fieldsService.prefix.variable, {enabled: true, filterType: FilterType.LIKE})
			.set(this.fieldsService.dippingRule.variable, {enabled: false, filterType: FilterType.LIKE})
			.set(this.fieldsService.senderId.variable, {enabled: true, filterType: FilterType.LIKE})
			.set(this.fieldsService.productName.variable, {enabled: true, filterType: FilterType.LIKE})
			.set(this.fieldsService.author.variable, {enabled: true, filterType: FilterType.LIKE})
			.set(this.fieldsService.created.variable, {enabled: true, filterType: FilterType.LIKE})
	}

	override ngOnInit(): void {
		super.ngOnInit();
		this.setRefBooksInFields({
			status: {
				list$: new BehaviorSubject([
					{ name: this.langService.translate('simulation.statuses.completed'), value: 'completed' },
					{ name: this.langService.translate('simulation.statuses.in_progress'), value: 'in_progress' },
					{ name: this.langService.translate('simulation.statuses.failed'), value: 'failed' }
				])
			},
			productName: {
				list$: this.productsNames$
			}
		});

		this.productsService
			.loadProducts(['hlr', 'sms'])
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((resp) => {
				this.products$.next(
					resp.Data.map(product => ({
						name: product.product_caption,
						value: product.product_id
					}))
				);
			});
		this.productsService
			.loadNames(['hlr', 'sms'])
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((resp) => {
				this.productsNames$.next(resp.Data);
			});
		this.users
			.load()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => {
				this.setRefBooksInFields({
					author: {
						list$: new BehaviorSubject<{ name: string; value: number }[]>(
							this.users.list.map((user) => {
								return {
									name: user.Login,
									value: user.ID
								};
							})
						)
					}
				});
			});
		this.simulationService.refresh$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((val) => {
			if (val) {
				this.refresh();
			}
		});
		if (
			this.simulationService.lastSimulationTask &&
			/^\/simulation_results\/\d+$/.test(this.authGuardService.previousUrl.getValue())
		) {
			this.viewResults(this.simulationService.lastSimulationTask);
		}
	}

	export(): void {
		this.exporting$.next(true);
		this.simulationService
			.export(this.total, this.state.readParams)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => {
				this.exporting$.next(false);
			});
	}

	createNewTask(): void {
		this.dialog
			.open<boolean>(CreateSimulationComponent, { autoFocus: false })
			.closed.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((result) => {
				if (result) {
					this.load();
				}
			});
	}

	delete(task: SiTask): void {
		this.dialog
			.open<boolean>(SimulationDeleteDialogComponent, { autoFocus: false, data: { task } })
			.closed.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((result) => {
				if (result) {
					this.load();
				}
			});
	}

	viewResults(task: SiTask): void {
		const products = this.products$.getValue();
		const product = products.find(p => p.value === task.productId);
		task.productLabel = product?.name ?? '—';
		this.dialog.open<boolean>(SimulationResultsComponent, {
			autoFocus: false,
			data: { task: task }
		});
	}

	formatDate(inputDate: string | undefined): string {
		if (inputDate) {
			const date = new Date(inputDate);
			const pad = (num: number): string => num.toString().padStart(2, '0');

			const day = pad(date.getDate());
			const month = pad(date.getMonth() + 1);
			const year = date.getFullYear();

			const hours = pad(date.getHours());
			const minutes = pad(date.getMinutes());
			const seconds = pad(date.getSeconds());

			return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
		} else {
			return '';
		}
	}
}
